import { useEffect, useRef, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import shuffle from 'lodash/shuffle';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import { CommentEditorContext } from '../../../../CommentEditor.editor_v3/context';
import { focusEditor } from '../../../../CommentEditor.editor_v3/redux/actionCreators';
import { DEVIATION_SUGGESTIONS_I18N_KEYS, POSTS_SUGGESTIONS_I18N_KEYS, MAX_VISIBLE_SUGGESTIONS_DESKTOP, MAX_VISIBLE_SUGGESTIONS_MOBILE } from '../constants';
import { CommentSuggestionsContext } from '../CommentSuggestionsContext';
export const useCommentSuggestions = (type)=>{
    const { t } = useTranslation();
    const dispatchFocusEditor = useAction(focusEditor);
    const { ref: editor, isReady: isEditorReady, isEmpty: isEditorEmpty } = useContext(EditorContext);
    const { editorId, expandEditor } = useContext(CommentEditorContext);
    const { suggestion: suggestionTranslationKey, setSuggestion, clearSuggestion } = useContext(CommentSuggestionsContext);
    const [isAddingSuggestion, setIsAddingSuggestion] = useState(false);
    const isMobile = useContext(MobileContext);
    // We have a set of pre-defined suggestions depending on `type`.
    // When this hook first runs we shuffle them and keep a reference to avoid repeating this process.
    const suggestionsRef = useRef(shuffle(type === 'deviation' ? DEVIATION_SUGGESTIONS_I18N_KEYS : type === 'posts' ? POSTS_SUGGESTIONS_I18N_KEYS : []));
    // When suggestion is selected, reset editor with its content, making sure editor has been loaded
    useEffect(()=>{
        if (suggestionTranslationKey && editor && isEditorReady && isAddingSuggestion) {
            const updateCallback = ()=>{
                dispatchFocusEditor(editorId);
                setIsAddingSuggestion(false);
            };
            editor.on('update', updateCallback);
            editor.commands.setContent(`${t(suggestionTranslationKey)} `, true);
            expandEditor();
            return ()=>{
                editor.off('update', updateCallback);
            };
        }
    }, [
        t,
        editor,
        suggestionTranslationKey,
        isEditorReady,
        editorId,
        dispatchFocusEditor,
        expandEditor,
        isEditorEmpty,
        isAddingSuggestion
    ]);
    // When editor content is cleared, we also clear the currently selected suggestion if there's any
    useEffect(()=>{
        isEditorEmpty && !isAddingSuggestion && clearSuggestion();
    }, [
        isEditorEmpty,
        isAddingSuggestion,
        clearSuggestion
    ]);
    // Define number of visible suggestions, depending on platform and features.
    let maxVisible;
    if (isMobile) {
        maxVisible = MAX_VISIBLE_SUGGESTIONS_MOBILE;
    } else {
        maxVisible = MAX_VISIBLE_SUGGESTIONS_DESKTOP;
    }
    const onSuggestionSelect = useCallback((selectedSuggestion)=>{
        let shouldAddSuggestion;
        if (editor && !isEditorEmpty && suggestionsRef.current.find((i18nKey)=>t(i18nKey) === editor.state.doc.textContent.trimEnd())) {
            shouldAddSuggestion = true;
        } else if (!isEditorEmpty) {
            shouldAddSuggestion = confirm(t('commentbox.editor.cancel.confirm'));
        } else {
            shouldAddSuggestion = true;
        }
        if (shouldAddSuggestion) {
            setSuggestion(selectedSuggestion);
            setIsAddingSuggestion(true);
        }
    }, [
        t,
        setSuggestion,
        editor,
        isEditorEmpty
    ]);
    return {
        suggestionKeys: suggestionsRef.current,
        maxVisible,
        onSuggestionSelect
    };
};

//# sourceMappingURL=useCommentSuggestions.js.map